"use client";

import React, { useMemo, useRef, useState } from "react";
import { AnchoredPopover } from "@/components/tailwind/ui/anchored-popover";
import { CornerDownLeft, MessageSquareText, ListOrdered, ChevronDown, Check } from "lucide-react";
import { useGlobalBlocks } from "@/contexts/blockContext";
import { updatePageSettingsMutation } from "@/indexedDB";
import type { PageSettings } from "@/types/block";
import { toast } from "@/lib/toast";

function ToggleSwitch({
  checked,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-[18px] w-[30px] flex-shrink-0 items-center rounded-full p-[2px] transition-colors ${
        checked ? "bg-blue-500" : "bg-gray-300 dark:bg-gray-600"
      }`}
    >
      <span
        className={`inline-block h-[14px] w-[14px] rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-[12px]" : "translate-x-0"
        }`}
      />
    </button>
  );
}

function InlineSelect<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (next: T) => void;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const current = options.find((o) => o.value === value);
  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-xs text-faint hover-component"
      >
        {current?.label ?? value}
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <AnchoredPopover
          anchor={triggerRef}
          onClose={() => setOpen(false)}
          placement="bottom-end"
          className="w-40 rounded-lg border border-component bg-modal p-1 text-popover-foreground shadow-lg outline-none"
        >
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              className="flex w-full items-center justify-between rounded px-2 py-1 text-sm text-gray-700 hover-component dark:text-gray-200 "
            >
              <span>{opt.label}</span>
              {opt.value === value && <Check className="h-4 w-4 text-faint" />}
            </button>
          ))}
        </AnchoredPopover>
      )}
    </>
  );
}

export default function CustomLayoutQuickPanel({ noteId }: { noteId: string }) {
  const { getBlock, updateBlock } = useGlobalBlocks();
  const block = getBlock(noteId);

  const pageSettings: PageSettings = useMemo(() => {
    return ((block?.value as any)?.pageSettings ?? {}) as PageSettings;
  }, [block]);

  const updateSetting = (partial: Partial<PageSettings>) => {
    if (!block) return;
    const merged: PageSettings = { ...pageSettings, ...partial };
    updateBlock(block._id as unknown as string, {
      value: { ...(block.value as any), pageSettings: merged } as any,
    });
    void updatePageSettingsMutation({
      blockId: noteId,
      workspaceId: block.workspaceId,
      settings: merged,
    }).catch((err) => {
      console.error("Failed to update page settings", err);
      toast.error("Failed to update layout");
    });
  };

  const backlinks = (pageSettings.backlinks ?? "show_on_hover") as
    | "show_on_hover"
    | "expanded"
    | "hidden";
  const pageDiscussions = pageSettings.pageDiscussions ?? true;
  const tableOfContents = pageSettings.tableOfContents ?? true;
  const inlineComments = (pageSettings.inlineComments ?? "default") as
    | "default"
    | "minimal"
    | "hidden";

  return (
    <div className="flex flex-col py-1">
      <div className="flex items-center gap-2 px-3 py-1.5">
        <CornerDownLeft className="h-4 w-4" />
        <span className="flex-1 text-sm text-gray-700 dark:text-gray-200">Backlinks</span>
        <InlineSelect
          value={backlinks}
          onChange={(v) => updateSetting({ backlinks: v })}
          options={[
            { value: "show_on_hover", label: "Show on hover" },
            { value: "expanded", label: "Expanded" },
            { value: "hidden", label: "Hidden" },
          ]}
        />
      </div>

      <div className="flex items-center gap-2 px-3 py-1.5">
        <MessageSquareText className="h-4 w-4" />
        <span className="flex-1 text-sm text-gray-700 dark:text-gray-200">Page discussions</span>
        <ToggleSwitch
          checked={!!pageDiscussions}
          ariaLabel="Page discussions"
          onChange={(next) => updateSetting({ pageDiscussions: next })}
        />
      </div>

      <div className="flex items-center gap-2 px-3 py-1.5">
        <ListOrdered className="h-4 w-4" />
        <span className="flex-1 text-sm text-gray-700 dark:text-gray-200">Table of contents</span>
        <ToggleSwitch
          checked={!!tableOfContents}
          ariaLabel="Table of contents"
          onChange={(next) => updateSetting({ tableOfContents: next })}
        />
      </div>

      <div className="flex items-center gap-2 px-3 py-1.5">
        <MessageSquareText className="h-4 w-4" />
        <span className="flex-1 text-sm text-gray-700 dark:text-gray-200">Inline comments</span>
        <InlineSelect
          value={inlineComments}
          onChange={(v) => updateSetting({ inlineComments: v })}
          options={[
            { value: "default", label: "Default" },
            { value: "minimal", label: "Minimal" },
            { value: "hidden", label: "Hidden" },
          ]}
        />
      </div>
    </div>
  );
}
